import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { inr, fmtDate } from "@/lib/utils-format";
import { PackageCheck, Loader2 } from "lucide-react";

export const turnoverBadge = (row) => {
  if (!row || row.turnover === "no_intake") return { label: "No intake", cls: "bg-stone-100 text-stone-600 border-stone-300" };
  if (row.turnover === "no_rate") return { label: `${row.qty_received} units · no HO rate set`, cls: "bg-amber-50 text-amber-700 border-amber-300" };
  if (row.turnover === "low") return { label: `Low turnover · ${Math.round(row.sales_to_intake_ratio * 100)}%`, cls: "bg-red-50 text-red-700 border-red-300" };
  if (row.turnover === "selling_down") return { label: `Selling down stock · ${Math.round(row.sales_to_intake_ratio * 100)}%`, cls: "bg-blue-50 text-blue-700 border-blue-300" };
  return { label: `Healthy · ${Math.round(row.sales_to_intake_ratio * 100)}%`, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" };
};

// Drill-down: every GRN a showroom received in the period
export const IntakeGrnDialog = ({ branch, dateFrom, dateTo, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!branch) return;
    setData(null);
    api.get(`/ho/showroom-intake/${branch.branch_id}/grns`, { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data)).catch(() => setData({ grns: [], count: 0, qty: 0, value: 0 }));
  }, [branch, dateFrom, dateTo]);
  return (
    <Dialog open={!!branch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="intake-grn-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-[#C05621]" /> Stock intake · {branch?.branch_name}
          </DialogTitle>
          <div className="text-xs text-[#8B7355]">{fmtDate(dateFrom)} → {fmtDate(dateTo)} · GRNs received from HO</div>
        </DialogHeader>
        {!data ? <div className="py-10 text-center text-[#8B7355]"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="space-y-3">
            <div className="flex gap-6 text-sm" data-testid="intake-grn-summary">
              <div><span className="uc-label">GRNs</span><div className="font-semibold">{data.count}</div></div>
              <div><span className="uc-label">Units</span><div className="font-semibold">{data.qty}</div></div>
              <div><span className="uc-label">Value (HO rate)</span><div className="font-semibold">{inr(data.value)}</div></div>
            </div>
            {data.grns.length === 0 && <div className="text-sm text-[#8B7355] py-6 text-center">No GRNs received in this period.</div>}
            {data.grns.map((g) => (
              <div key={g.id} className="border border-[#EAE5D9] rounded-md" data-testid={`intake-grn-row-${g.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[#FDFBF7]">
                  <div className="text-sm"><span className="font-mono font-semibold">{g.number}</span> <span className="text-xs text-[#8B7355]">· from {g.dispatch_number || "—"} · {fmtDate(g.received_at)}{g.received_by_name ? ` · signed ${g.received_by_name}` : ""}</span></div>
                  <div className="text-sm font-semibold">{g.qty} units · {inr(g.value)}</div>
                </div>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-[#EAE5D9]">
                    {g.items.map((it, i) => (
                      <tr key={i}><td className="px-3 py-1.5">{it.name}</td><td className="px-3 py-1.5 text-right">{it.received_qty}{it.dispatched_qty !== it.received_qty && <Badge variant="outline" className="ml-1 text-[10px] bg-amber-50 text-amber-700 border-amber-300">sent {it.dispatched_qty}</Badge>}</td><td className="px-3 py-1.5 text-right text-[#8B7355]">@ {inr(it.unit_cost)}</td><td className="px-3 py-1.5 text-right font-medium">{inr(it.value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
